/**
 * Flux de compte : création, déverrouillage, changement de mot de passe maître.
 *
 * API recommandée pour les applications (web, Android, iOS) : elle compose les
 * primitives dans le bon ordre, de sorte qu'un développeur applicatif n'ait
 * jamais à manipuler nonce, AAD ni contexte AEAD à la main.
 */
import { DEFAULT_KDF_PARAMS, type AeadAlgorithmId, type KdfParams } from './constants.js';
import {
  deriveAccountKeys,
  deriveAccountKeysAsync,
  destroyAccountKeys,
  generateKdfSaltBase64,
  type AccountKeys,
} from './kdf.js';
import {
  generateVaultKey,
  protectVaultKey,
  rewrapVaultKey,
  unprotectVaultKey,
  type ProtectedSymmetricKey,
} from './protected-key.js';
import {
  deriveRecoveryAuthProofBase64,
  formatRecoveryKey,
  generateRecoveryKey,
  parseRecoveryKey,
  protectVaultKeyWithRecoveryKey,
  unprotectVaultKeyWithRecoveryKey,
  type RecoveryProtectedSymmetricKey,
} from './recovery-key.js';
import { InvalidParameterError } from './errors.js';
import type { SecretKey } from './secret.js';

export interface CreateAccountInput {
  readonly masterPassword: string;
  /**
   * E-mail du compte. ABSENT ou vide : compte sans e-mail, le sel Argon2id est
   * tiré au hasard et rangé dans `kdfParams.saltBase64`. Présent : sel dérivé
   * de l'e-mail, comme pour tous les comptes antérieurs.
   */
  readonly email?: string;
  readonly kdfParams?: KdfParams;
  readonly algorithm?: AeadAlgorithmId;
}

/**
 * Résultat de la création de compte.
 *
 * À transmettre au serveur : `email`, `kdfParams`, `masterPasswordHashBase64`,
 * `protectedSymmetricKey`. RIEN d'autre.
 * À garder en mémoire locale uniquement : `vaultKey`.
 */
export interface CreatedAccount {
  readonly email: string;
  readonly kdfParams: KdfParams;
  readonly masterPasswordHashBase64: string;
  readonly protectedSymmetricKey: ProtectedSymmetricKey;
  readonly vaultKey: SecretKey;
}

function isBlank(email: string | undefined): boolean {
  return email === undefined || (typeof email === 'string' && email.trim() === '');
}

/** Sans e-mail, le compte reçoit un sel aléatoire (sauf si les paramètres en portent déjà un). */
function creationKdfParams(email: string | undefined, kdfParams: KdfParams | undefined): KdfParams {
  const params = kdfParams ?? DEFAULT_KDF_PARAMS;
  if (!isBlank(email) || params.saltBase64 !== undefined) return params;
  return { ...params, saltBase64: generateKdfSaltBase64() };
}

function buildAccount(keys: AccountKeys, algorithm: AeadAlgorithmId | undefined): CreatedAccount {
  const vaultKey = generateVaultKey();
  try {
    const protectedSymmetricKey = protectVaultKey({
      vaultKey,
      stretchedMasterKey: keys.stretchedMasterKey,
      algorithm,
    });
    return {
      email: keys.email,
      kdfParams: keys.kdfParams,
      masterPasswordHashBase64: keys.masterPasswordHashBase64,
      protectedSymmetricKey,
      vaultKey,
    };
  } catch (cause) {
    vaultKey.destroy();
    throw cause;
  } finally {
    // masterKey et stretchedMasterKey ne servent plus : on les efface.
    destroyAccountKeys(keys);
  }
}

/**
 * Crée le matériel cryptographique d'un compte : dérivation Argon2id, `vaultKey`
 * aléatoire, protected symmetric key.
 */
export function createAccount(input: CreateAccountInput): CreatedAccount {
  const keys = deriveAccountKeys({
    masterPassword: input?.masterPassword,
    email: input?.email,
    params: creationKdfParams(input?.email, input?.kdfParams),
  });
  return buildAccount(keys, input.algorithm);
}

/** Variante asynchrone de {@link createAccount} (ne bloque pas l'interface). */
export async function createAccountAsync(input: CreateAccountInput): Promise<CreatedAccount> {
  const keys = await deriveAccountKeysAsync({
    masterPassword: input?.masterPassword,
    email: input?.email,
    params: creationKdfParams(input?.email, input?.kdfParams),
  });
  return buildAccount(keys, input.algorithm);
}

export interface UnlockVaultInput {
  readonly masterPassword: string;
  /** Obligatoire pour un compte antérieur (sel = e-mail) ; facultatif si `kdfParams.saltBase64`. */
  readonly email?: string;
  /** Paramètres KDF du compte, tels que renvoyés par le serveur. */
  readonly kdfParams: KdfParams;
  /** Protected symmetric key du compte, telle que stockée par le serveur. */
  readonly protectedSymmetricKey: ProtectedSymmetricKey;
}

export interface UnlockedVault {
  readonly email: string;
  /** Clé de chiffrement du coffre, déverrouillée en mémoire. */
  readonly vaultKey: SecretKey;
  /** À présenter au serveur pour ouvrir une session. */
  readonly masterPasswordHashBase64: string;
}

function finishUnlock(keys: AccountKeys, protectedSymmetricKey: ProtectedSymmetricKey): UnlockedVault {
  try {
    const vaultKey = unprotectVaultKey({
      protectedKey: protectedSymmetricKey,
      stretchedMasterKey: keys.stretchedMasterKey,
    });
    return {
      email: keys.email,
      vaultKey,
      masterPasswordHashBase64: keys.masterPasswordHashBase64,
    };
  } finally {
    destroyAccountKeys(keys);
  }
}

/**
 * Déverrouille le coffre.
 *
 * @throws {DecryptionFailedError} mot de passe maître ou e-mail incorrect,
 *   paramètres KDF incorrects, protected symmetric key altérée
 */
export function unlockVault(input: UnlockVaultInput): UnlockedVault {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  const keys = deriveAccountKeys({
    masterPassword: input.masterPassword,
    email: input.email,
    params: input.kdfParams,
  });
  return finishUnlock(keys, input.protectedSymmetricKey);
}

/** Variante asynchrone de {@link unlockVault}. */
export async function unlockVaultAsync(input: UnlockVaultInput): Promise<UnlockedVault> {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  const keys = await deriveAccountKeysAsync({
    masterPassword: input.masterPassword,
    email: input.email,
    params: input.kdfParams,
  });
  return finishUnlock(keys, input.protectedSymmetricKey);
}

export interface ChangeMasterPasswordInput {
  /** Obligatoire pour un compte antérieur (sel = e-mail) ; facultatif si `kdfParams.saltBase64`. */
  readonly email?: string;
  readonly currentMasterPassword: string;
  readonly newMasterPassword: string;
  readonly kdfParams: KdfParams;
  readonly protectedSymmetricKey: ProtectedSymmetricKey;
  /** Paramètres KDF du nouveau mot de passe (défaut : les mêmes). */
  readonly newKdfParams?: KdfParams;
  /** Nouvel e-mail (changement d'e-mail = changement de sel). */
  readonly newEmail?: string;
  readonly algorithm?: AeadAlgorithmId;
}

export interface ChangedMasterPassword {
  readonly email: string;
  readonly kdfParams: KdfParams;
  readonly masterPasswordHashBase64: string;
  readonly protectedSymmetricKey: ProtectedSymmetricKey;
}

/**
 * Changement effectué EN CONNAISSANT le mot de passe actuel : le résultat porte
 * aussi sa preuve, qu'un serveur exige en plus de la nouvelle (l'ancienne pour
 * autoriser, la nouvelle pour remplacer). La dériver ici évite une troisième
 * passe Argon2id côté application. Absente de la réinitialisation par clé de
 * récupération, qui par construction ne connaît pas le mot de passe actuel.
 */
export interface ChangedMasterPasswordWithProof extends ChangedMasterPassword {
  readonly currentMasterPasswordHashBase64: string;
}

/**
 * Change le mot de passe maître : la `vaultKey` est re-scellée sous la nouvelle
 * `stretchedMasterKey`. Le coffre lui-même n'est PAS re-chiffré.
 *
 * @throws {DecryptionFailedError} mot de passe maître actuel incorrect
 */
export function changeMasterPassword(input: ChangeMasterPasswordInput): ChangedMasterPasswordWithProof {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  const current = deriveAccountKeys({
    masterPassword: input.currentMasterPassword,
    email: input.email,
    params: input.kdfParams,
  });
  let next: AccountKeys;
  try {
    next = deriveAccountKeys({
      masterPassword: input.newMasterPassword,
      email: input.newEmail ?? input.email,
      params: input.newKdfParams ?? input.kdfParams,
    });
  } catch (cause) {
    // Le nouveau mot de passe est refusé (vide, paramètres KDF faibles…) :
    // les clés déjà dérivées de l'ANCIEN ne doivent pas survivre à l'échec.
    destroyAccountKeys(current);
    throw cause;
  }
  return finishChange(input, current, next);
}

/** Variante asynchrone de {@link changeMasterPassword} (ne bloque pas l'interface). */
export async function changeMasterPasswordAsync(input: ChangeMasterPasswordInput): Promise<ChangedMasterPasswordWithProof> {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  const current = await deriveAccountKeysAsync({
    masterPassword: input.currentMasterPassword,
    email: input.email,
    params: input.kdfParams,
  });
  let next: AccountKeys;
  try {
    next = await deriveAccountKeysAsync({
      masterPassword: input.newMasterPassword,
      email: input.newEmail ?? input.email,
      params: input.newKdfParams ?? input.kdfParams,
    });
  } catch (cause) {
    destroyAccountKeys(current);
    throw cause;
  }
  return finishChange(input, current, next);
}

/** Re-scelle la `vaultKey` de l'ancienne vers la nouvelle dérivation, puis efface les deux jeux de clés. */
function finishChange(input: ChangeMasterPasswordInput, current: AccountKeys, next: AccountKeys): ChangedMasterPasswordWithProof {
  try {
    const protectedSymmetricKey = rewrapVaultKey({
      protectedKey: input.protectedSymmetricKey,
      currentStretchedMasterKey: current.stretchedMasterKey,
      nextStretchedMasterKey: next.stretchedMasterKey,
      algorithm: input.algorithm,
    });
    return {
      email: next.email,
      kdfParams: input.newKdfParams ?? input.kdfParams,
      masterPasswordHashBase64: next.masterPasswordHashBase64,
      protectedSymmetricKey,
      currentMasterPasswordHashBase64: current.masterPasswordHashBase64,
    };
  } finally {
    destroyAccountKeys(current);
    destroyAccountKeys(next);
  }
}

export interface EnabledRecoveryKey {
  /**
   * Clé de récupération en clair — à afficher/imprimer UNE SEULE FOIS puis
   * effacer (`.destroy()`). Klavox ne la conserve jamais.
   */
  readonly recoveryKey: SecretKey;
  /** Forme lisible à présenter à l'utilisateur (base32 groupé). */
  readonly recoveryKeyDisplay: string;
  /** À transmettre au serveur : la `vaultKey` scellée sous la clé de récupération. */
  readonly recoveryProtectedSymmetricKey: RecoveryProtectedSymmetricKey;
  /** À transmettre au serveur avec l'enveloppe : il l'exigera pour réinitialiser (preuve de possession). */
  readonly recoveryAuthProofBase64: string;
}

/**
 * Active la récupération de compte : génère une clé de récupération de 128
 * bits et y scelle la `vaultKey` du coffre déjà déverrouillé.
 *
 * L'appelant DOIT afficher `recoveryKeyDisplay` à l'utilisateur (impression,
 * copie hors-ligne) puis appeler `recoveryKey.destroy()` — Klavox ne stocke
 * jamais la clé de récupération en clair.
 */
export function enableRecoveryKey(
  vaultKey: SecretKey,
  algorithm?: AeadAlgorithmId,
): EnabledRecoveryKey {
  const recoveryKey = generateRecoveryKey();
  try {
    const recoveryProtectedSymmetricKey = protectVaultKeyWithRecoveryKey({
      vaultKey,
      recoveryKey,
      algorithm,
    });
    return {
      recoveryKey,
      recoveryKeyDisplay: formatRecoveryKey(recoveryKey),
      recoveryProtectedSymmetricKey,
      recoveryAuthProofBase64: deriveRecoveryAuthProofBase64(recoveryKey),
    };
  } catch (cause) {
    recoveryKey.destroy();
    throw cause;
  }
}

export interface ResetMasterPasswordWithRecoveryKeyInput {
  /** Clé de récupération telle que saisie par l'utilisateur (tolérante au formatage). */
  readonly recoveryKeyDisplay: string;
  /** `vaultKey` scellée sous la clé de récupération, telle que stockée par le serveur. */
  readonly recoveryProtectedSymmetricKey: RecoveryProtectedSymmetricKey;
  /** Absent ou vide : compte sans e-mail, un NOUVEAU sel aléatoire est tiré. */
  readonly email?: string;
  readonly newMasterPassword: string;
  /** Paramètres KDF du nouveau mot de passe (défaut : `DEFAULT_KDF_PARAMS`, plus un sel aléatoire sans e-mail). */
  readonly newKdfParams?: KdfParams;
  readonly algorithm?: AeadAlgorithmId;
}

/** Réinitialisation par clé de récupération : la preuve de possession part au serveur avec les nouveaux identifiants. */
export interface ResetMasterPasswordWithRecoveryKeyResult extends ChangedMasterPassword {
  readonly recoveryAuthProofBase64: string;
}

/**
 * Réinitialise le mot de passe maître SANS le connaître, via la clé de
 * récupération : ouvre la `vaultKey` par la voie de récupération, puis la
 * re-scelle sous le nouveau mot de passe maître. Le coffre lui-même n'est
 * pas touché — mêmes garanties que {@link changeMasterPassword}.
 *
 * @throws {DecryptionFailedError} clé de récupération incorrecte, ou
 *   `recoveryProtectedSymmetricKey` altérée
 * @throws {InvalidParameterError} clé de récupération mal formée
 */
export function resetMasterPasswordWithRecoveryKey(
  input: ResetMasterPasswordWithRecoveryKeyInput,
): ResetMasterPasswordWithRecoveryKeyResult {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  const recoveryKey = parseRecoveryKey(input.recoveryKeyDisplay);
  let vaultKey: SecretKey;
  let recoveryAuthProofBase64: string;
  try {
    vaultKey = unprotectVaultKeyWithRecoveryKey({
      protectedKey: input.recoveryProtectedSymmetricKey,
      recoveryKey,
    });
    recoveryAuthProofBase64 = deriveRecoveryAuthProofBase64(recoveryKey);
  } finally {
    recoveryKey.destroy();
  }
  const nextParams = creationKdfParams(input.email, input.newKdfParams);
  const next = deriveAccountKeys({
    masterPassword: input.newMasterPassword,
    email: input.email,
    params: nextParams,
  });
  try {
    const protectedSymmetricKey = protectVaultKey({
      vaultKey,
      stretchedMasterKey: next.stretchedMasterKey,
      algorithm: input.algorithm,
    });
    return {
      email: next.email,
      kdfParams: nextParams,
      masterPasswordHashBase64: next.masterPasswordHashBase64,
      protectedSymmetricKey,
      recoveryAuthProofBase64,
    };
  } finally {
    vaultKey.destroy();
    destroyAccountKeys(next);
  }
}
